import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import {
  Bot,
  ArrowRight,
  Shield,
  Zap,
  Sliders,
  Check,
  X,
  Sparkles,
  MessageSquare,
  Gavel,
  ChevronLeft,
  ChevronRight,
  Map,
  LayoutDashboard,
  Smartphone,
  Gamepad2,
  ScrollText,
  Lightbulb,
  Gift,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { staggerContainer, staggerItem } from "@/lib/motion";

const PILLARS = [
  {
    icon: Shield,
    title: "Protect",
    text: "Layered safety: moderation, spam and invite controls, anti-nuke and raid alerts, verification, logs — tuned from the dashboard.",
  },
  {
    icon: Zap,
    title: "Automate",
    text: "Welcome, autorole, tickets, giveaways, economy commands — fewer repetitive tasks for staff.",
  },
  {
    icon: Sliders,
    title: "Own your config",
    text: "One responsive web panel per server: save, sync, and delegate access without touching JSON by hand.",
  },
] as const;

const MODULES = [
  {
    title: "Moderation Suite",
    blurb: "Warn, timeout, kick, ban, purge, mod log channel — built for busy staff.",
    tag: "Free",
  },
  {
    title: "Ticket Suite",
    blurb: "Setup ticket panels, button categories, transcript logs, Dev/Admin roles completely free! (AI Ticket Pilot is premium)",
    tag: "Free",
  },
  {
    title: "Suggestions System",
    blurb: "Accept community suggestions with automated upvote/downvote buttons and custom tracking channels completely free!",
    tag: "Free",
  },
  {
    title: "Security & safety",
    blurb:
      "Anti-spam, automod, invite filter, anti-nuke, anti-raid, verification with /verification_panel, whitelist/blacklist — plus bypass roles.",
    tag: "Free",
  },
  {
    title: "Automation & onboarding",
    blurb: "Welcome & goodbye messages, autorole setups, interactive reaction roles, and automated daily messages completely free!",
    tag: "Free",
  },
  {
    title: "Polls & surveys",
    blurb: "Staff-managed polls, voting channels, duration limits, and live results completely free!",
    tag: "Free",
  },
  {
    title: "Giveaways",
    blurb: "Post premium giveaways, set bypass roles, verify requirements, and track active raffles completely free!",
    tag: "Free",
  },
  {
    title: "Games & economy",
    blurb:
      "Daily claim allowances, minigames (Roulette, Blackjack, Slots, Coinflip, Mines, Battleship, …) completely free in English!",
    tag: "Free",
  },
  {
    title: "Logging suite",
    blurb: "Route joins, edits, deletes, bans, channel edits, and role changes to specialized log channels.",
    tag: "Free",
  },
] as const;

const COMPARISON: { f: string; us: "yes" | "partial" | "soon" | "no"; them: "yes" | "partial" | "no" }[] = [
  { f: "Full web panel (no code)", us: "yes", them: "partial" },
  { f: "Free Tickets & Suggestions panel", us: "yes", them: "no" },
  { f: "Onboarding: welcome flow + reaction roles", us: "yes", them: "partial" },
  { f: "Giveaways, Polls & Daily Messages (Free)", us: "yes", them: "no" },
  { f: "Games & economy without Premium", us: "yes", them: "no" },
  { f: "AI Support Pilot (Tickets / FAQ) ‡", us: "yes", them: "no" },
  { f: "Deep security stack (spam → anti-nuke → raid)", us: "yes", them: "partial" },
];

function moduleIcon(title: string) {
  if (title.includes("Moderation")) return Gavel;
  if (title.includes("Ticket")) return MessageSquare;
  if (title.includes("Security") || title.includes("safety")) return Shield;
  if (title.includes("Automation")) return Zap;
  if (title.includes("Polls") || title.includes("surveys")) return MessageSquare;
  if (title.includes("Games") || title.includes("economy")) return Gamepad2;
  if (title.includes("Logging")) return ScrollText;
  if (title.includes("Suggestions")) return Lightbulb;
  if (title.includes("Giveaways")) return Gift;
  return Sparkles;
}

function Cell({ v }: { v: "yes" | "partial" | "soon" | "no" }) {
  if (v === "yes") {
    return (
      <div className="flex justify-center">
        <span className="h-6 w-6 rounded-full bg-primary/15 flex items-center justify-center">
          <Check className="h-3.5 w-3.5 text-primary" strokeWidth={3} />
        </span>
      </div>
    );
  }
  if (v === "partial") {
    return <span className="block text-center text-primary/80 text-sm font-medium">~</span>;
  }
  if (v === "soon") {
    return (
      <div className="flex justify-center">
        <Badge className="bg-cyan-500/12 text-cyan-300 border-cyan-600/35 text-[10px]">Soon</Badge>
      </div>
    );
  }
  return (
    <div className="flex justify-center text-muted-foreground/40">
      <X className="h-4 w-4" />
    </div>
  );
}

function formatNum(n: number) {
  return new Intl.NumberFormat("en-GB", { maximumFractionDigits: 0 }).format(n);
}

export function Landing() {
  const { isAuthLoading, isLoggedIn, user } = useAuth();
  const [previewSlide, setPreviewSlide] = useState(0);

  const { data: stats, isError, isLoading: statsLoading } = useQuery({
    queryKey: ["publicStats"],
    queryFn: () => api.publicStats(),
    staleTime: 0,
  });

  const primaryCta = isAuthLoading ? (
    <Button size="lg" disabled>
      Verifying sign-in…
    </Button>
  ) : isLoggedIn && user ? (
    <Link to="/dashboard">
      <Button size="lg">
        Open dashboard
        <ArrowRight className="h-4 w-4" />
      </Button>
    </Link>
  ) : (
    <a href="/login">
      <Button size="lg">
        <Bot className="h-4 w-4" />
        Get started with Discord
      </Button>
    </a>
  );

  return (
    <div className="space-y-16 md:space-y-24 pb-8">
      {/* ===== HERO — asymmetric split ===== */}
      <section className="grid lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-14 items-center pt-4 lg:pt-10">
        {/* Left: copy */}
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <Badge variant="default" className="border-primary/40">
              <Sparkles className="h-3 w-3" />
              All-in-one Discord bot
            </Badge>
            {stats && (
              <span className="text-[11px] font-semibold text-primary tracking-wide uppercase">
                Currently serving {stats.guild_count} servers
              </span>
            )}
          </div>

          <h1 className="mt-5 text-4xl sm:text-5xl xl:text-6xl font-semibold tracking-tight leading-[1.05]">
            Automate, protect, and{" "}
            <span className="text-gradient">grow your server</span>
          </h1>

          <p className="mt-5 text-lg text-foreground/90 max-w-xl">
            Less chaos. More control. A safer Discord.
          </p>
          <p className="mt-2 text-sm sm:text-base text-muted-foreground max-w-xl leading-relaxed">
            Tickets, security, games, and logging — configured in one place instead of juggling half a dozen bots.
          </p>

          <div className="flex flex-wrap gap-3 pt-7">
            {primaryCta}
            <Button size="lg" variant="outline" asChild>
              <Link to="/roadmap">
                <Map className="h-4 w-4" />
                Roadmap
              </Link>
            </Button>
          </div>

          <p className="mt-6 text-xs font-medium tracking-wide text-muted-foreground">
            <span className="text-foreground/70">Automation</span> ·{" "}
            <span className="text-foreground/70">Security</span> ·{" "}
            <span className="text-foreground/70">Management</span>
          </p>
        </div>

        {/* Right: floating glass mock */}
        <div className="relative">
          <div className="pointer-events-none absolute -inset-6 bg-gradient-to-br from-primary/20 via-primary/5 to-transparent blur-3xl rounded-full" />
          <motion.div
            initial={{ opacity: 0, y: 16, rotateX: 6 }}
            animate={{ opacity: 1, y: 0, rotateX: 0 }}
            transition={{ duration: 0.5 }}
            className="relative"
          >
            <Card className="p-4 border-primary/20 bg-card/70 backdrop-blur-xl shadow-2xl">
              {/* fake window bar */}
              <div className="flex items-center gap-1.5 pb-3 mb-3 border-b border-border/40">
                <span className="h-2.5 w-2.5 rounded-full bg-red-400/70" />
                <span className="h-2.5 w-2.5 rounded-full bg-amber-400/70" />
                <span className="h-2.5 w-2.5 rounded-full bg-emerald-400/70" />
                <span className="ml-2 text-[11px] text-muted-foreground font-mono">areon · dashboard</span>
              </div>
              {/* live stat */}
              <div className="rounded-xl bg-primary/10 ring-1 ring-primary/20 p-4 flex items-center justify-between mb-3">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Servers online</p>
                  <p className="text-3xl font-semibold tracking-tight text-primary tabular-nums">
                    {stats ? formatNum(stats.guild_count) : "—"}
                  </p>
                </div>
                <div className="h-10 w-10 rounded-xl bg-primary/20 flex items-center justify-center">
                  <Bot className="h-5 w-5 text-primary" />
                </div>
              </div>
              {/* fake toggles */}
              {[
                { icon: MessageSquare, label: "Ticket system", on: true },
                { icon: Shield, label: "Security & anti-nuke", on: true },
                { icon: Gamepad2, label: "Games & economy", on: true },
              ].map((r) => {
                const Icon = r.icon;
                return (
                  <div key={r.label} className="flex items-center gap-3 py-2.5 px-1 border-b border-border/30 last:border-0">
                    <div className="h-8 w-8 rounded-lg bg-muted/40 flex items-center justify-center">
                      <Icon className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <span className="flex-1 text-sm font-medium">{r.label}</span>
                    <span className="h-5 w-9 rounded-full bg-primary/80 relative">
                      <span className="absolute right-0.5 top-0.5 h-4 w-4 rounded-full bg-white" />
                    </span>
                  </div>
                );
              })}
            </Card>
          </motion.div>
        </div>
      </section>

      {/* ===== STATS — inline divided strip ===== */}
      <section aria-label="Public bot stats">
        <div className="rounded-2xl border border-border/50 bg-card/40 grid grid-cols-2 sm:grid-cols-4 divide-x divide-y sm:divide-y-0 divide-border/40 overflow-hidden">
          {[
            { label: "Active servers", v: stats && !isError ? formatNum(stats.guild_count) : "—" },
            { label: "Commands (slash+)", v: stats && !isError ? `~${stats.commands_approx}` : "—" },
            { label: "Uptime*", v: stats && !isError && stats.uptime_display && stats.uptime_display !== "—" ? stats.uptime_display : "n/a" },
            {
              label: "Tickets / support",
              v: stats && !isError && stats.tickets_line ? stats.tickets_line : stats && !isError ? "Global" : "—",
            },
          ].map((item) => (
            <div key={item.label} className="p-5 text-center">
              <div className="text-2xl sm:text-3xl font-semibold tracking-tight tabular-nums">{item.v}</div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground mt-1">{item.label}</div>
            </div>
          ))}
        </div>
        <p className="text-center text-sm text-foreground/80 mt-4 max-w-2xl mx-auto">
          {isError
            ? "Public stats aren’t available right now. Try again later."
            : statsLoading
              ? "Loading live numbers…"
              : stats?.trusted_line ?? "Live signal — who’s on board"}
        </p>
        <p className="text-[11px] text-center text-muted-foreground/70 mt-1.5">
          Figures are approximate and refresh about every 90s; they may differ slightly from the live state.
        </p>
      </section>

      {/* ===== PILLARS — numbered ===== */}
      <section className="grid md:grid-cols-3 gap-4">
        {PILLARS.map((p, i) => {
          const Icon = p.icon;
          return (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.35, delay: i * 0.05 }}
            >
              <Card className="h-full p-6 border-border/50 bg-card/40 relative overflow-hidden">
                <span className="absolute top-4 right-5 text-5xl font-bold text-primary/5 select-none">
                  0{i + 1}
                </span>
                <div className="h-10 w-10 rounded-xl bg-primary/10 ring-1 ring-primary/15 flex items-center justify-center mb-4">
                  <Icon className="h-5 w-5 text-primary" />
                </div>
                <h3 className="font-semibold text-lg mb-1.5">{p.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{p.text}</p>
              </Card>
            </motion.div>
          );
        })}
      </section>

      {/* ===== PREVIEW carousel ===== */}
      <section aria-label="Product preview mockup">
        <div className="text-center mb-6">
          <h2 className="text-2xl md:text-3xl font-semibold tracking-tight">What the dashboard looks like</h2>
          <p className="text-sm text-muted-foreground max-w-xl mx-auto mt-2">
            Static mock below — the real app runs after you sign in. Use the tabs to preview different screens.
          </p>
        </div>
        <div className="relative rounded-2xl border border-border/50 bg-card/40 p-3">
          <div className="flex flex-wrap justify-center gap-1.5 mb-3">
            {(
              [
                { id: 0, label: "Panel", icon: LayoutDashboard },
                { id: 1, label: "Ticket / embed", icon: MessageSquare },
                { id: 2, label: "Mobile", icon: Smartphone },
              ] as const
            ).map(({ id, label, icon: Icon }) => (
              <button
                key={label}
                type="button"
                onClick={() => setPreviewSlide(id)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg transition-colors ${
                  previewSlide === id
                    ? "bg-primary/15 text-primary"
                    : "text-muted-foreground hover:bg-accent/40"
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                {label}
              </button>
            ))}
          </div>
          <div className="flex items-center justify-center gap-3 mb-3">
            <Button type="button" size="icon" variant="outline" onClick={() => setPreviewSlide((p) => (p + 2) % 3)} aria-label="Previous slide">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="h-1.5 flex-1 max-w-sm rounded-full bg-muted/60 overflow-hidden">
              <motion.div className="h-full bg-primary/70" animate={{ width: `${((previewSlide + 1) / 3) * 100}%` }} transition={{ duration: 0.25 }} />
            </div>
            <Button type="button" size="icon" variant="outline" onClick={() => setPreviewSlide((p) => (p + 1) % 3)} aria-label="Next slide">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <div className="min-h-[280px]">
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={previewSlide}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.2 }}
                className="rounded-xl border border-border/50 bg-background/40 p-1"
              >
                {previewSlide === 0 && (
                  <div className="grid md:grid-cols-[minmax(140px,200px)_1fr] min-h-[260px] rounded-lg overflow-hidden">
                    <div className="p-3 border-r border-border/50 space-y-1.5 text-left bg-muted/30">
                      {["My servers", "Tickets", "Mod", "Security", "Welcome", "Games", "Settings"].map((l, i) => (
                        <div key={l} className={`px-2 py-1.5 rounded text-xs font-medium ${i === 0 ? "bg-primary/15 text-primary" : "text-muted-foreground"}`}>
                          {l}
                        </div>
                      ))}
                    </div>
                    <div className="p-4 sm:p-5 text-left">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">My server</p>
                      <h4 className="text-lg font-semibold text-foreground mt-0.5 mb-3">Config — save &amp; apply</h4>
                      <div className="space-y-2">
                        <div className="h-2.5 w-[85%] rounded-sm bg-primary/30" />
                        <div className="h-2.5 w-[60%] rounded-sm bg-muted-foreground/25" />
                        <div className="h-2.5 w-[72%] rounded-sm bg-muted-foreground/25" />
                        <p className="text-[11px] text-muted-foreground pt-2">
                          Changes sync to Discord within seconds (real product — this image is a preview).
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                {previewSlide === 1 && (
                  <div className="p-4 sm:p-6 min-h-[260px] flex items-center justify-center">
                    <div className="w-full max-w-md rounded-lg border-l-4 border-l-[#5865F2] p-4 shadow-lg" style={{ background: "linear-gradient(180deg, #f4f5f7, #e8eaef)" }}>
                      <p className="text-[#5865F2] text-xs font-semibold">Areon · app message</p>
                      <p className="text-zinc-800 text-sm mt-2">
                        Support ticket: describe your issue; staff is notified in your ticket channel.
                      </p>
                      <div className="mt-3 flex flex-wrap gap-2 text-[10px] text-zinc-500">
                        <span className="rounded bg-zinc-200 px-1.5 py-0.5">#support</span>
                        <span>·</span>
                        <span>Today 8:00 PM</span>
                      </div>
                    </div>
                  </div>
                )}
                {previewSlide === 2 && (
                  <div className="min-h-[260px] flex items-center justify-center p-4 sm:p-6">
                    <div className="w-[min(100%,240px)] rounded-[2rem] border-4 border-border bg-muted/60 p-2 shadow-xl">
                      <div className="rounded-[1.4rem] bg-card border border-border/80 p-3 text-center min-h-[220px] flex flex-col">
                        <div className="mx-auto w-8 h-1 rounded-full bg-muted-foreground/25 mb-3" aria-hidden />
                        <Smartphone className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                        <p className="text-sm font-medium text-foreground">Browser on phone</p>
                        <p className="text-xs text-muted-foreground mt-2 flex-1 leading-relaxed">
                          The same dashboard is responsive. Open it from your phone; no app install required.
                        </p>
                        <div className="h-1.5 w-12 mx-auto mt-2 rounded-full bg-muted-foreground/20" aria-hidden />
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </section>

      {/* ===== MODULES — icon-left grid ===== */}
      <section>
        <div className="text-center mb-8">
          <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-2">What you get</h2>
          <p className="text-sm text-muted-foreground max-w-2xl mx-auto">
            Suites map to real dashboard tabs: turn features on per server, tune wording and channels, delegate manager roles where supported.
          </p>
        </div>
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-30px" }}
          className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3.5"
        >
          {MODULES.map((m) => {
            const Icon = moduleIcon(m.title);
            return (
              <motion.div key={m.title} variants={staggerItem}>
                <Card className="group h-full p-5 border-border/50 bg-card/40 hover:bg-card/70 hover:border-primary/30 transition-colors">
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 rounded-xl bg-primary/10 ring-1 ring-primary/15 flex items-center justify-center shrink-0 group-hover:bg-primary/15 transition-colors">
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="font-medium text-sm">{m.title}</h3>
                        {m.tag === "Free" && (
                          <span className="text-[9px] font-semibold uppercase tracking-wider text-emerald-500/80 shrink-0">Free</span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{m.blurb}</p>
                    </div>
                  </div>
                </Card>
              </motion.div>
            );
          })}
        </motion.div>
      </section>

      {/* ===== COMPARISON ===== */}
      <section>
        <div className="text-center mb-6">
          <h2 className="text-2xl md:text-3xl font-semibold tracking-tight">Why Areon — one stack, fewer bots</h2>
          <p className="text-sm text-muted-foreground max-w-2xl mx-auto mt-2">
            Illustrative comparison vs generic multipurpose bots. Premium vs free modules depend on your host’s checkout. ‡ AI ticket assist is planned — see Roadmap.
          </p>
        </div>
        <div className="max-w-2xl mx-auto overflow-hidden rounded-2xl border border-border/50 bg-card/40">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border/40 text-left">
                <th className="p-3.5 pl-4 font-medium text-xs uppercase tracking-wider text-muted-foreground">Area</th>
                <th className="p-3.5 text-center w-28 bg-primary/5 font-semibold text-primary text-xs uppercase tracking-wider">Areon</th>
                <th className="p-3.5 text-center w-28 pr-4 font-medium text-xs uppercase tracking-wider text-muted-foreground">Others</th>
              </tr>
            </thead>
            <tbody>
              {COMPARISON.map((r) => (
                <tr key={r.f} className="border-b border-border/30 last:border-0">
                  <td className="p-3.5 pl-4 text-[13px] text-foreground/80">{r.f}</td>
                  <td className="p-3.5 text-center bg-primary/5">
                    <Cell v={r.us} />
                  </td>
                  <td className="p-3.5 text-center pr-4">
                    <Cell v={r.them} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-center text-muted-foreground mt-4">
          Roadmap and timeline:{" "}
          <Link to="/roadmap" className="text-primary hover:underline">
            Roadmap
          </Link>
        </p>
      </section>

      {/* ===== Positioning + CTA ===== */}
      <section>
        <div className="relative overflow-hidden rounded-3xl border border-border/50 bg-gradient-to-br from-primary/12 via-card to-card p-8 sm:p-12 text-center">
          <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-primary/20 blur-3xl" />
          <div className="pointer-events-none absolute -left-16 -bottom-16 h-56 w-56 rounded-full bg-primary/10 blur-3xl" />
          <div className="relative max-w-2xl mx-auto">
            <h3 className="text-2xl sm:text-3xl font-semibold tracking-tight">Invite Areon and ship your setup</h3>
            <p className="text-sm text-muted-foreground mt-3 leading-relaxed">
              One powerful web dashboard for your entire Discord community. Enjoy a massive suite of 100% FREE features: ticket panels, suggestions, reaction roles, giveaways, polls, daily automated messages, welcome and goodbye greetings, and full English economy games. Upgrade to Premium only when you need advanced AI automation (like our AI Ticket Pilot or Ticket Feedback analytics), deep Moderation, logging, and comprehensive server security.
            </p>
            <div className="flex flex-wrap justify-center gap-3 pt-6">
              {primaryCta}
              <Button size="lg" variant="outline" asChild>
                <Link to="/roadmap">Roadmap</Link>
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-5">
              Servers running it today:
              {stats && stats.guild_count > 0 ? ` ≥ ${formatNum(stats.guild_count)}. ` : " count them with us. "}
              {stats?.trusted_line}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
